/**
 * @module @app/shared/files/parser/csv/observable
 * @description CSV parser for observable files.
 * @link {https://www.notion.so/metroscope/RFC-File-Parsing-Guideline-d4be80786e4a4a329a5a807fa71fb585?pvs=4}
 * Expected format:
 *
 * | name or technicalName | activatedObservable | observableSigmaDiagnosis |
 * | --------------------- | ------------------- | ------------------------ |
 * | string                | 0 or 1              | float gte 0               |
 * |                       |                     |                          |
 *
 */

import type { Result } from "../../../../lib/patterns/result";
import { Err, Ok, isResultErr } from "../../../../lib/patterns/result";
import type { FILE_PARSING_ERROR } from "../../common/file";
import { parseFile } from "../../common/file";
import type { ParsingResult, ParsingError, ParsingErrorMessageMap, Parser } from "../../types";
import type { Input } from "../../../../api/call";
import {
  columns,
  hasDuplicatedRowByQuantityId,
  //   hasExtraColumns,
  hasInvalidActivatedObservableValue,
  hasInvalidColumns,
  hasInvalidSigmaForActivatedObservable,
  hasInvalidSigmaValue,
  hasNegativeSigma,
  hasNoValueForQuantityId,
  hasUnexpectedQuantityId,
  shouldIgnoreEmptyActivatedObservable,
  shouldSkipDefinedObservable,
} from "./rules";
import { hasTimeFunctionDependency } from "diagnostics-lib/runtime";

/**
 * @description Error that can be thrown by the observable parser.
 */
export enum OBSERVABLE_PARSING_ERROR {
  FILE_INVALID_CONTENT = "FILE_INVALID_CONTENT",
  DATA_DUPLICATED = "DATA_DUPLICATED",
  DATA_QUANTITY_ID_MISSING = "DATA_QUANTITY_ID_MISSING",
  DATA_QUANTITY_ID_NOT_ALLOWED = "DATA_QUANTITY_ID_NOT_ALLOWED",
  DATA_ACTIVATED_INVALID = "DATA_ACTIVATED_INVALID",
  DATA_SIGMA_INVALID = "DATA_SIGMA_INVALID",
  DATA_SIGMA_POSITIVE = "DATA_SIGMA_POSITIVE",
  DATA_SIGMA_ACTIVATED_REQUIRED = "DATA_SIGMA_ACTIVATED_REQUIRED",
  HAS_TIME_FUNCTION_DEPENDENCY = "HAS_TIME_FUNCTION_DEPENDENCY",
}

/**
 * @description Data that can be parsed from an observable file.
 */
export type ObservableParsingResultData = Input<
  "POST",
  "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/inputs"
>["body"]["inputs"];

export type ObservableParsingResult = ParsingResult<ObservableParsingResultData>;
export type ObservableParsingError = ParsingError<OBSERVABLE_PARSING_ERROR | FILE_PARSING_ERROR>;
export type ObservableParsingErrorMessageMap = ParsingErrorMessageMap<
  OBSERVABLE_PARSING_ERROR | FILE_PARSING_ERROR
>;

/**
 * @description Parse observable file content data.
 * @param file Observable file to parse
 * @param data File content, excluding the csv header defining columns
 * @param expectedNameValues Expected values for the name|technicalName column
 * @returns
 */
const parseData = (
  file: File,
  data: (string | undefined)[][],
  expectedNameValues: string[],
  expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  },
): Result<ObservableParsingResultData, ObservableParsingError[]> => {
  const errors: ObservableParsingError[] = [];
  const observables = [];

  for (let line = 0; line < data.length; line++) {
    const [quantityId = "", activatedObservable = "", observableSigmaDiagnosis = ""] =
      data[line] || [];

    if (
      expressionEngineInput.graph &&
      hasTimeFunctionDependency(
        [quantityId],
        expressionEngineInput.corpus,
        expressionEngineInput.graph,
      )
    )
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.HAS_TIME_FUNCTION_DEPENDENCY,
        explain: 'invalid "past_value" call, cannot use another time formula as a parameter',
        location: { file: file.name, line: line + 1 },
        data: {
          key: "name",
          value: quantityId,
        },
      });

    if (hasNoValueForQuantityId(quantityId))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_QUANTITY_ID_MISSING,
        explain: "file missing value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          value: quantityId,
          hint: expectedNameValues.join(", "),
        },
      });

    if (hasUnexpectedQuantityId(quantityId, expectedNameValues))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_QUANTITY_ID_NOT_ALLOWED,
        explain: "file contains unexpected value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: expectedNameValues.join(", "),
          value: quantityId,
        },
      });

    if (shouldIgnoreEmptyActivatedObservable(activatedObservable)) continue;

    if (hasInvalidActivatedObservableValue(activatedObservable))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_ACTIVATED_INVALID,
        explain: "file contains unexpected value for column 'activatedObservable'",
        location: { file: file.name, line },
        data: {
          key: "activatedObservable",
          hint: quantityId,
          value: activatedObservable,
        },
      });

    if (shouldSkipDefinedObservable(activatedObservable, observableSigmaDiagnosis)) continue;

    if (hasInvalidSigmaForActivatedObservable(activatedObservable, observableSigmaDiagnosis))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_SIGMA_ACTIVATED_REQUIRED,
        explain:
          "value for column 'observableSigmaDiagnosis' must be a defined if value for 'activatedObservable' equals 1",
        location: { file: file.name, line },
        data: {
          key: "observableSigmaDiagnosis",
          hint: quantityId,
          value: observableSigmaDiagnosis,
        },
      });

    if (hasInvalidSigmaValue(observableSigmaDiagnosis))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_SIGMA_INVALID,
        explain: "value for column 'observableSigmaDiagnosis' must be a number",
        location: { file: file.name, line },
        data: {
          key: "observableSigmaDiagnosis",
          hint: quantityId,
          value: observableSigmaDiagnosis,
        },
      });

    if (hasNegativeSigma(observableSigmaDiagnosis))
      errors.push({
        kind: OBSERVABLE_PARSING_ERROR.DATA_SIGMA_POSITIVE,
        explain: "value for column 'observableSigmaDiagnosis' must be positive",
        location: { file: file.name, line },
        data: {
          key: "observableSigmaDiagnosis",
          hint: quantityId,
          value: observableSigmaDiagnosis,
        },
      });

    /**
     * Everything is fine, we can add the observable to the list.
     */
    observables.push({
      quantityId,
      enabled: Boolean(parseInt(activatedObservable)),
      sigma: parseFloat(observableSigmaDiagnosis),
    });
  }

  return errors.length > 0 ? Err(errors) : Ok(observables);
};

/**
 * @description Parse observable file content.
 * @param file Observable file to parse
 * @param param1.expectedNameValues Expected values for the name|technicalName column Record<quantityName, quantityId>
 * @returns
 */
export const parse: Parser<
  ObservableParsingResult,
  ObservableParsingError[],
  {
    expectedNameValues: string[];
    quantityIdByName: Record<string, string>;
    expressionEngineInput: {
      corpus: Record<string, string | null>;
      graph: {
        [token: string]: string[] | null;
      } | null;
    };
  }
> = async (file, { expectedNameValues, quantityIdByName, expressionEngineInput }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: ObservableParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  const content = fileResult.value as string;

  if (hasInvalidColumns(content))
    errors.push({
      kind: OBSERVABLE_PARSING_ERROR.FILE_INVALID_CONTENT,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        value: content
          .split("\n")
          .map(line => line.replace(/\r/, "").split(";"))[0]
          ?.join(", "),
      },
    });

  const [header, ...data] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );

  /**
   * Helium format contain a lot of columns, we try to filter them to use only useful ones.
   * And map data accordingly.
   */
  const columnIndices = header
    ?.map((col, i) => (columns.some(names => names.includes(col)) ? i : null))
    .filter(i => i !== null);

  const targetData = data
    .map(line => line.filter((_, j) => columnIndices?.includes(j)))
    .filter(line => line.length === 3);

  if (!targetData || targetData.length === 0)
    return Err([
      {
        kind: OBSERVABLE_PARSING_ERROR.FILE_INVALID_CONTENT,
        explain: "file don't contains expected columns",
        location: { file: file.name, line: 0 },
        data: {
          value: content
            .split("\n")
            .map(line => line.replace(/\r/, "").split(";"))[0]
            ?.join(", "),
        },
      },
    ]);

  const quantityIds = targetData.map(([quantityId] = []) => quantityId).filter(Boolean);

  if (hasDuplicatedRowByQuantityId(quantityIds))
    errors.push({
      kind: OBSERVABLE_PARSING_ERROR.DATA_DUPLICATED,
      explain: "file contain duplicated row",
      location: { file: file.name, line: 0 },
      data: {
        key: "name",
        value: quantityIds.find((quantityId, index) => quantityIds.indexOf(quantityId) !== index),
      },
    });

  /**
   * @description Check data.
   * @see {parseData}
   */
  const dataResult = parseData(file, targetData, expectedNameValues, expressionEngineInput);

  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        version: header?.[0] === "name" ? "lithium" : "helium",
        data: (dataResult.value as ObservableParsingResultData).map(v => ({
          ...v,
          quantityId: quantityIdByName[v.quantityId] as string,
        })),
      });
};
